// Word-generatorer för Bas-U:s utförandedokument (Dok 06b–34).
// Samma tema, stämpel, revisionsrad och exportlogg som övriga generatorer.
import type { Paragraph, Table as DocxTable } from "docx";
import { supabase } from "@/integrations/supabase/client";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  h1,
  h2,
  factTable,
  table,
  fmtDate,
  fmtDateTime,
  val,
} from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

const safe = (name?: string) => (name || "projekt").replace(/[^\w-]+/g, "_");

/** Hämtar projektnamnet så att komponenterna slipper skicka med det. */
export const getProjectName = async (projectId: string): Promise<string> => {
  const { data } = await supabase.from("projects").select("name").eq("id", projectId).maybeSingle();
  return data?.name ?? "";
};

interface EmitOptions {
  projectId: string;
  docNumber: string;
  documentType: string;
  documentName: string;
  fileSlug: string;
  snapshot: unknown;
  build: (projectName: string) => (Paragraph | DocxTable)[];
}

/** Gemensam väg ut: stämpel, sidfot, dokument och nedladdning med logg. */
const emit = async ({
  projectId,
  docNumber,
  documentType,
  documentName,
  fileSlug,
  snapshot,
  build,
}: EmitOptions) => {
  const projectName = await getProjectName(projectId);
  const stamp = await createStampForCurrentUser({ docNumber, documentName, projectName });
  const footer = await documentFooter({ docNumber, documentType, projectId });

  const doc = buildDocument({
    meta: { review: footer.review, docNumber, documentName, projectName },
    stamp,
    children: [...build(projectName), ...footer.children],
  });

  await downloadWordDocument(doc, `Dok${docNumber}_${fileSlug}_${safe(projectName)}.docx`, {
    documentType,
    docNumber,
    documentName,
    snapshot,
    stamp,
  });
};

const flag = (text: string, open: boolean) => (open ? `⚠ ${text}` : text);

const emptyNote = (text: string) => body(text, { muted: true, italics: true });

/* ---------------- Dok 06b – Utrustningsbesiktningar ---------------- */

export interface EquipmentInspectionRow {
  equipment_type: string;
  object_name: string;
  location?: string | null;
  inspection_type: string;
  interval_days?: number | null;
  last_inspected?: string | null;
  next_inspection?: string | null;
  result?: string | null;
  inspector?: string | null;
  certificate_ref?: string | null;
  status: string;
  notes?: string | null;
}

const isOverdue = (next?: string | null) =>
  !!next && new Date(next).getTime() < Date.now();

export const generateEquipmentInspectionsWordDocument = (
  projectId: string,
  rows: EquipmentInspectionRow[]
) =>
  emit({
    projectId,
    docNumber: "06b",
    documentType: "equipment_inspections",
    documentName: "Besiktning och kontroll av utrustning",
    fileSlug: "Utrustningsbesiktningar",
    snapshot: rows,
    build: () => {
      const overdue = rows.filter((r) => isOverdue(r.next_inspection));
      return [
        h1("Besiktade objekt"),
        rows.length
          ? table(
              ["Typ", "Objekt", "Plats", "Kontroll", "Senast", "Nästa", "Resultat", "Besiktningsman", "Intyg", "Status"],
              rows.map((r) => [
                r.equipment_type,
                flag(r.object_name, isOverdue(r.next_inspection)),
                r.location,
                r.inspection_type,
                fmtDate(r.last_inspected),
                fmtDate(r.next_inspection),
                r.result,
                r.inspector,
                r.certificate_ref,
                r.status,
              ])
            )
          : emptyNote("Inga besiktningar registrerade."),
        h1("Åtgärdsläge"),
        body(
          overdue.length
            ? `${overdue.length} objekt har passerat sitt besiktningsdatum och får inte användas förrän ny kontroll är utförd: ${overdue
                .map((r) => r.object_name)
                .join(", ")}.`
            : "Samtliga registrerade objekt har giltig besiktning."
        ),
        ...rows
          .filter((r) => r.notes)
          .map((r) => body(`${r.object_name}: ${r.notes}`)),
      ];
    },
  });

/* ---------------- Dok 07 – Tredje man och pågående verksamhet ---------------- */

export interface ThirdPartyMeasure {
  area: string;
  affected: string;
  risk: string;
  measure: string;
  responsible: string;
  done: boolean;
}

export interface ThirdPartyContent {
  situation?: string;
  occupied?: boolean;
  operationsRunning?: boolean;
  nightWork?: boolean;
  evacuation?: string;
  tempInstallations?: string;
  information?: string;
  contact?: string;
  measures?: ThirdPartyMeasure[];
}

const yn = (v?: boolean) => (v ? "Ja" : "Nej");

export const generateThirdPartyPlanWordDocument = (
  projectId: string,
  content: ThirdPartyContent,
  meta?: { status?: string; lastReviewAt?: string | null; lastReviewBy?: string | null }
) =>
  emit({
    projectId,
    docNumber: "07",
    documentType: "third_party_plan",
    documentName: "Tredje man och pågående verksamhet",
    fileSlug: "Tredje_man",
    snapshot: content,
    build: () => {
      const measures = content.measures ?? [];
      const open = measures.filter((m) => !m.done);
      return [
        h1("Förutsättningar"),
        factTable([
          ["Status", val(meta?.status)],
          ["Verksamhet pågår i byggnaden", yn(content.occupied)],
          ["Drift pågår intill arbetsområdet", yn(content.operationsRunning)],
          ["Nattarbete förekommer", yn(content.nightWork)],
          ["Senast genomgången", meta?.lastReviewAt ? fmtDate(meta.lastReviewAt) : "–"],
          ["Genomgången av", val(meta?.lastReviewBy)],
        ]),
        h1("Situationsbeskrivning"),
        body(val(content.situation)),
        h1("Utrymning och tillfälliga installationer"),
        h2("Utrymning"),
        body(val(content.evacuation)),
        h2("Tillfälliga installationer"),
        body(val(content.tempInstallations)),
        h1("Information och kontakt"),
        h2("Information till berörda"),
        body(val(content.information)),
        h2("Kontaktväg"),
        body(val(content.contact)),
        h1("Skyddsåtgärder"),
        measures.length
          ? table(
              ["Område", "Berörda", "Risk", "Åtgärd", "Ansvarig", "Status"],
              measures.map((m) => [
                flag(m.area, !m.done),
                m.affected,
                m.risk,
                m.measure,
                m.responsible,
                m.done ? "Utförd" : "Öppen",
              ])
            )
          : emptyNote("Inga skyddsåtgärder registrerade."),
        body(
          open.length
            ? `${open.length} åtgärder är fortfarande öppna och ska följas upp på nästa skyddsrond.`
            : "Samtliga åtgärder är utförda."
        ),
      ];
    },
  });

/* ---------------- Dok 07b – TA-plan ---------------- */

export interface TrafficPlanRow {
  title: string;
  area?: string | null;
  responsible_name?: string | null;
  responsible_email?: string | null;
  authority?: string | null;
  permit_ref?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  measures?: string | null;
  status: string;
  stageName?: string | null;
}

export const generateTrafficPlanWordDocument = (projectId: string, rows: TrafficPlanRow[]) =>
  emit({
    projectId,
    docNumber: "07b",
    documentType: "traffic_plan",
    documentName: "TA-plan – trafikanordningsplan",
    fileSlug: "TA_plan",
    snapshot: rows,
    build: () =>
      rows.length
        ? rows.flatMap((r) => {
            const expired = !!r.valid_to && new Date(r.valid_to).getTime() < Date.now();
            return [
              h1(flag(r.title, expired)),
              factTable([
                ["Etapp", val(r.stageName)],
                ["Område", val(r.area)],
                ["Ansvarig", [r.responsible_name, r.responsible_email].filter(Boolean).join(" · ") || "–"],
                ["Myndighet", val(r.authority)],
                ["Tillståndsnummer", val(r.permit_ref)],
                ["Giltig från", fmtDate(r.valid_from)],
                ["Giltig till", expired ? `${fmtDate(r.valid_to)} (utgången)` : fmtDate(r.valid_to)],
                ["Status", val(r.status)],
              ]),
              h2("Trafikanordningar och åtgärder"),
              body(val(r.measures)),
            ];
          })
        : [h1("TA-planer"), emptyNote("Inga TA-planer registrerade.")],
  });

/* ---------------- Dok 08 – Farligt material och sanering ---------------- */

export interface HazmatSurveyMeta {
  status?: string;
  surveyor?: string | null;
  survey_date?: string | null;
  lab_name?: string | null;
  notes?: string | null;
}

export interface HazmatItemRow {
  material: string;
  location?: string | null;
  sample_ref?: string | null;
  result: string;
  quantity?: string | null;
  action?: string | null;
  responsible?: string | null;
  permit_ref?: string | null;
  waste_doc_ref?: string | null;
  receiver?: string | null;
  status: string;
}

export const generateHazmatSurveyWordDocument = (
  projectId: string,
  survey: HazmatSurveyMeta,
  items: HazmatItemRow[]
) =>
  emit({
    projectId,
    docNumber: "08",
    documentType: "hazmat_survey",
    documentName: "Farligt material och sanering",
    fileSlug: "Farligt_material",
    snapshot: { survey, items },
    build: () => {
      const open = items.filter((i) => i.status !== "sanerad" && i.status !== "avslutad");
      return [
        h1("Inventeringen"),
        factTable([
          ["Status", val(survey.status)],
          ["Inventerad av", val(survey.surveyor)],
          ["Inventeringsdatum", fmtDate(survey.survey_date)],
          ["Laboratorium", val(survey.lab_name)],
        ]),
        survey.notes ? body(survey.notes) : emptyNote("Inga kommentarer."),
        h1("Fynd och hantering"),
        items.length
          ? table(
              ["Material", "Plats", "Prov", "Resultat", "Mängd", "Åtgärd", "Ansvarig", "Tillstånd", "Avfallsdok.", "Mottagare", "Status"],
              items.map((i) => [
                flag(i.material, open.includes(i)),
                i.location,
                i.sample_ref,
                i.result,
                i.quantity,
                i.action,
                i.responsible,
                i.permit_ref,
                i.waste_doc_ref,
                i.receiver,
                i.status,
              ])
            )
          : emptyNote("Inga fynd registrerade."),
        h1("Kvarstående"),
        body(
          open.length
            ? `${open.length} fynd är inte färdigsanerade. Arbete i berörda ytor får inte påbörjas innan sanering är utförd och dokumenterad.`
            : "Samtliga fynd är sanerade och dokumenterade."
        ),
      ];
    },
  });

/* ---------------- Dok 09 – Frånkoppling och återinkoppling ---------------- */

export interface IsolationRow {
  system_name: string;
  isolation_point?: string | null;
  isolation_type: string;
  lock_id?: string | null;
  isolated_by?: string | null;
  isolated_at?: string | null;
  verified_dead_by?: string | null;
  restored_by?: string | null;
  restored_at?: string | null;
  status: string;
  notes?: string | null;
}

export const generateIsolationsWordDocument = (projectId: string, rows: IsolationRow[]) =>
  emit({
    projectId,
    docNumber: "09",
    documentType: "electrical_isolation",
    documentName: "Frånkoppling och återinkoppling",
    fileSlug: "Frankoppling",
    snapshot: rows,
    build: () => {
      const stillIsolated = rows.filter((r) => r.status === "isolated");
      const unverified = rows.filter((r) => r.status === "isolated" && !r.verified_dead_by);
      return [
        h1("Frånkopplade system"),
        rows.length
          ? table(
              ["System", "Frånkopplingspunkt", "Typ", "Lås-ID", "Frånkopplad av", "Tidpunkt", "Spänningslöshet verifierad av", "Återinkopplad av", "Tidpunkt", "Status"],
              rows.map((r) => [
                flag(r.system_name, r.status === "isolated" && !r.verified_dead_by),
                r.isolation_point,
                r.isolation_type,
                r.lock_id,
                r.isolated_by,
                fmtDateTime(r.isolated_at),
                r.verified_dead_by,
                r.restored_by,
                fmtDateTime(r.restored_at),
                r.status,
              ])
            )
          : emptyNote("Inga frånkopplingar registrerade."),
        h1("Läge"),
        body(
          `${stillIsolated.length} system är frånkopplade och låsta just nu. ${
            unverified.length
              ? `${unverified.length} av dessa saknar dokumenterad verifiering av spänningslöshet – arbete får inte påbörjas där.`
              : "Samtliga frånkopplingar är verifierade."
          }`
        ),
        body("Återinkoppling får endast göras av den som satt låset, eller efter dokumenterad överlämning.", {
          italics: true,
        }),
        ...rows.filter((r) => r.notes).map((r) => body(`${r.system_name}: ${r.notes}`)),
      ];
    },
  });

/* ---------------- Dok 09b – Avbrottsplan ---------------- */

export interface OutagePlanRow {
  utility: string;
  area?: string | null;
  affected_operations?: string | null;
  planned_start?: string | null;
  planned_end?: string | null;
  backup_measures?: string | null;
  notified_parties?: string | null;
  responsible_name?: string | null;
  status: string;
  notes?: string | null;
}

export const generateOutagePlansWordDocument = (projectId: string, rows: OutagePlanRow[]) =>
  emit({
    projectId,
    docNumber: "09b",
    documentType: "utility_outage_plan",
    documentName: "Avbrottsplan – el, gas och media",
    fileSlug: "Avbrottsplan",
    snapshot: rows,
    build: () => [
      h1("Planerade avbrott"),
      rows.length
        ? table(
            ["Media", "Område", "Berörd verksamhet", "Start", "Slut", "Reservåtgärder", "Underrättade", "Ansvarig", "Status"],
            rows.map((r) => [
              flag(r.utility, r.status === "planned"),
              r.area,
              r.affected_operations,
              fmtDateTime(r.planned_start),
              fmtDateTime(r.planned_end),
              r.backup_measures,
              r.notified_parties,
              r.responsible_name,
              r.status,
            ])
          )
        : emptyNote("Inga avbrott planerade."),
      h1("Kommentarer"),
      ...(rows.some((r) => r.notes)
        ? rows.filter((r) => r.notes).map((r) => body(`${r.utility}: ${r.notes}`))
        : [emptyNote("Inga kommentarer.")]),

    ],
  });

/* ---------------- Dok 15 – Skydds- och ordningsregler ---------------- */

export interface SafetyRuleItemRow {
  category: string;
  text: string;
  legal_reference?: string | null;
  included: boolean;
  sort_order?: number;
}

export const generateSafetyRulesWordDocument = (
  projectId: string,
  doc: { name?: string; intro?: string | null; status?: string },
  items: SafetyRuleItemRow[]
) =>
  emit({
    projectId,
    docNumber: "15",
    documentType: "safety_rules",
    documentName: doc.name || "Skydds- och ordningsregler",
    fileSlug: "Skyddsregler",
    snapshot: { doc, items },
    build: () => {
      const included = items.filter((i) => i.included);
      const categories = Array.from(new Set(included.map((i) => i.category)));
      return [
        ...(doc.intro ? [h1("Inledning"), body(doc.intro)] : []),
        ...categories.flatMap((cat) => [
          h1(cat),
          table(
            ["Regel", "Lagrum"],
            included
              .filter((i) => i.category === cat)
              .map((i) => [i.text, i.legal_reference]),
            { columnWidths: [7360, 2000] }
          ),
        ]),
        ...(included.length ? [] : [emptyNote("Inga regler valda ännu.")]),
        h1("Kvittens"),
        body(
          "Genom att kvittera reglerna intygar jag att jag tagit del av dem och följer dem under hela vistelsen på arbetsplatsen."
        ),
        table(["Namn", "Företag", "Datum", "Signatur"], [["", "", "", ""], ["", "", "", ""], ["", "", "", ""]]),
      ];
    },
  });

/* ---------------- Dok 15b – Kvittensuppföljning ---------------- */

export interface AckRow {
  company: string;
  email: string;
  stage: string;
  documents: { label: string; acknowledgedAt: string | null }[];
}

export const generateAcknowledgementsWordDocument = (projectId: string, rows: AckRow[]) =>
  emit({
    projectId,
    docNumber: "15b",
    documentType: "acknowledgements",
    documentName: "Kvittensuppföljning per etapp",
    fileSlug: "Kvittenser",
    snapshot: rows,
    build: () => {
      const labels = rows[0]?.documents.map((d) => d.label) ?? [];
      const missing = rows.filter((r) => r.documents.some((d) => !d.acknowledgedAt));
      return [
        h1("Kvittensläge"),
        rows.length
          ? table(
              ["Företag", "Kontakt", "Etapp", ...labels],
              rows.map((r) => [
                flag(r.company, r.documents.some((d) => !d.acknowledgedAt)),
                r.email,
                r.stage,
                ...r.documents.map((d) => (d.acknowledgedAt ? fmtDate(d.acknowledgedAt) : "saknas")),
              ])
            )
          : emptyNote("Inga entreprenörer registrerade."),
        h1("Saknade kvittenser"),
        body(
          missing.length
            ? `${missing.length} företag saknar kvittens: ${missing.map((m) => m.company).join(", ")}. Arbete får inte påbörjas innan kvittens finns.`
            : "Samtliga företag har kvitterat."
        ),
      ];
    },
  });

/* ---------------- Dok 15c – Säkerhetsintroduktion ---------------- */

export interface InductionRow {
  person_name: string;
  company?: string | null;
  trade?: string | null;
  induction_date: string;
  language: string;
  held_by?: string | null;
  valid_to?: string | null;
  topics: string[];
  notes?: string | null;
  stageName?: string | null;
}

export const generateInductionsWordDocument = (projectId: string, rows: InductionRow[]) =>
  emit({
    projectId,
    docNumber: "15c",
    documentType: "safety_induction",
    documentName: "Säkerhetsintroduktion per person",
    fileSlug: "Sakerhetsintroduktion",
    snapshot: rows,
    build: () => {
      const expired = rows.filter((r) => r.valid_to && new Date(r.valid_to).getTime() < Date.now());
      return [
        h1("Genomförda introduktioner"),
        rows.length
          ? table(
              ["Namn", "Företag", "Yrke", "Etapp", "Datum", "Språk", "Hållen av", "Giltig till", "Innehåll"],
              rows.map((r) => [
                flag(r.person_name, !!r.valid_to && new Date(r.valid_to).getTime() < Date.now()),
                r.company,
                r.trade,
                r.stageName,
                fmtDate(r.induction_date),
                r.language,
                r.held_by,
                fmtDate(r.valid_to),
                (r.topics || []).join(", "),
              ])
            )
          : emptyNote("Inga introduktioner registrerade."),
        h1("Giltighet"),
        body(
          expired.length
            ? `${expired.length} personer har utgången introduktion och ska genomgå ny innan fortsatt arbete: ${expired
                .map((r) => r.person_name)
                .join(", ")}.`
            : "Samtliga registrerade introduktioner är giltiga."
        ),
      ];
    },
  });

/* ---------------- Dok 16b – Heta arbeten ---------------- */

export interface HotWorkRow {
  location: string;
  work_description: string;
  contractor?: string | null;
  permit_issuer?: string | null;
  hot_work_certificate_ref?: string | null;
  fire_watch_name?: string | null;
  fire_watch_after_minutes?: number | null;
  valid_from?: string | null;
  valid_to?: string | null;
  precautions: string[];
  status: string;
  notes?: string | null;
}

export const generateHotWorkPermitsWordDocument = (projectId: string, rows: HotWorkRow[]) =>
  emit({
    projectId,
    docNumber: "16b",
    documentType: "hot_work_permit",
    documentName: "Heta arbeten – tillstånd",
    fileSlug: "Heta_arbeten",
    snapshot: rows,
    build: () =>
      rows.length
        ? rows.flatMap((r, idx) => [
            h1(`Tillstånd ${idx + 1}: ${r.location}`),
            factTable([
              ["Arbetsmoment", val(r.work_description)],
              ["Entreprenör", val(r.contractor)],
              ["Tillståndsansvarig", val(r.permit_issuer)],
              ["Certifikat heta arbeten", val(r.hot_work_certificate_ref)],
              ["Brandvakt", val(r.fire_watch_name)],
              ["Efterbevakning", r.fire_watch_after_minutes ? `${r.fire_watch_after_minutes} minuter` : "–"],
              ["Giltigt från", fmtDateTime(r.valid_from)],
              ["Giltigt till", fmtDateTime(r.valid_to)],
              ["Status", val(r.status)],
            ]),
            h2("Säkerhetsåtgärder"),
            table(
              ["Åtgärd", "Uppfylld"],
              (r.precautions || []).length
                ? r.precautions.map((p) => [p, "Ja"])
                : [["Inga åtgärder ikryssade", "⚠ Nej"]],
              { columnWidths: [7360, 2000] }
            ),
            ...(r.notes ? [body(r.notes)] : []),
            h2("Underskrifter"),
            table(
              ["Roll", "Namn", "Datum", "Signatur"],
              [
                ["Tillståndsansvarig", val(r.permit_issuer), "", ""],
                ["Utförande", val(r.contractor), "", ""],
                ["Brandvakt", val(r.fire_watch_name), "", ""],
              ]
            ),
          ])
        : [h1("Heta arbeten"), emptyNote("Inga tillstånd utfärdade.")],
  });

/* ---------------- Dok 21 – Uppföljning av utförandet ---------------- */

export interface FollowupRow {
  area?: string | null;
  title: string;
  status: string;
  statusLabel: string;
  responsible?: string | null;
  checked_at?: string | null;
  comment?: string | null;
}

export const generateExecutionFollowupWordDocument = (projectId: string, rows: FollowupRow[]) =>
  emit({
    projectId,
    docNumber: "21",
    documentType: "execution_followup",
    documentName: "Uppföljning av utförandet",
    fileSlug: "Uppfoljning_utforande",
    snapshot: rows,
    build: () => {
      const open = rows.filter((r) => r.status !== "klar" && r.status !== "ej_aktuell");
      return [
        h1("Uppföljningspunkter"),
        rows.length
          ? table(
              ["Område", "Punkt", "Status", "Ansvarig", "Kontrollerad", "Kommentar"],
              rows.map((r) => [
                r.area,
                flag(r.title, r.status !== "klar" && r.status !== "ej_aktuell"),
                r.statusLabel,
                r.responsible,
                fmtDate(r.checked_at),
                r.comment,
              ])
            )
          : emptyNote("Inga punkter registrerade."),
        h1("Öppna punkter"),
        body(
          open.length
            ? `${open.length} av ${rows.length} punkter är inte avslutade och följs upp löpande på skyddsronderna.`
            : "Samtliga punkter är avslutade."
        ),
      ];
    },
  });

/* ---------------- Dok 26 – Kemikalier och säkerhetsdatablad ---------------- */

export interface SdsRow {
  product_name: string;
  supplier?: string | null;
  usage_area?: string | null;
  quantity?: string | null;
  hazard_codes?: string | null;
  protective_measures?: string | null;
  contractor?: string | null;
  file_name?: string | null;
}

export const generateSafetyDataSheetsWordDocument = (projectId: string, rows: SdsRow[]) =>
  emit({
    projectId,
    docNumber: "26",
    documentType: "safety_data_sheets",
    documentName: "Kemikalieförteckning och säkerhetsdatablad",
    fileSlug: "Kemikalieforteckning",
    snapshot: rows,
    build: () => {
      const missing = rows.filter((r) => !r.file_name);
      return [
        h1("Kemikalieförteckning"),
        rows.length
          ? table(
              ["Produkt", "Leverantör", "Användningsområde", "Mängd", "Faroangivelser", "Skyddsåtgärder", "Entreprenör", "Säkerhetsdatablad"],
              rows.map((r) => [
                flag(r.product_name, !r.file_name),
                r.supplier,
                r.usage_area,
                r.quantity,
                r.hazard_codes,
                r.protective_measures,
                r.contractor,
                r.file_name || "saknas",
              ])
            )
          : emptyNote("Inga kemiska produkter registrerade."),
        h1("Saknade säkerhetsdatablad"),
        body(
          missing.length
            ? `${missing.length} produkter saknar säkerhetsdatablad: ${missing
                .map((r) => r.product_name)
                .join(", ")}. Produkterna får inte användas innan bladet finns på plats.`
            : "Samtliga produkter har säkerhetsdatablad i registret."
        ),
      ];
    },
  });

/* ---------------- Dok 34 – Slutdokumentation ---------------- */

export interface HandoverRow {
  docNumber: string;
  title: string;
  owner: string;
  detail: string;
  ok: boolean;
}

export const generateHandoverIndexWordDocument = (
  projectId: string,
  rows: HandoverRow[],
  percent: number
) =>
  emit({
    projectId,
    docNumber: "34",
    documentType: "handover_index",
    documentName: "Slutdokumentation till förvaltning",
    fileSlug: "Slutdokumentation",
    snapshot: { rows, percent },
    build: () => {
      const missing = rows.filter((r) => !r.ok);
      return [
        h1("Innehållsförteckning"),
        body(`${percent} % av innehållet är på plats vid utskriftstillfället.`),
        table(
          ["Dok", "Dokument", "Ansvarig roll", "Innehåll", "Status"],
          rows.map((r) => [
            r.docNumber,
            flag(r.title, !r.ok),
            r.owner,
            r.detail,
            r.ok ? "Klart" : "Saknas",
          ])
        ),
        h1("Kvarstår före överlämning"),
        missing.length
          ? table(
              ["Dok", "Dokument", "Ansvarig roll"],
              missing.map((r) => [r.docNumber, r.title, r.owner])
            )
          : body("Inget kvarstår – dokumentationen är komplett."),
        h1("Överlämning"),
        table(
          ["Roll", "Namn", "Datum", "Signatur"],
          [
            ["Bas-U", "", "", ""],
            ["Byggherre eller förvaltare", "", "", ""],
          ]
        ),
      ];
    },
  });
